import { useMutation } from '@apollo/client';
import { Button, Loader } from '@ets-global/b2c-website-ui';
import { fromJS } from 'immutable';
import React, { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import AccountBlock from '../../../components/Account/Block';
import { SessionCandidate, SessionDetail, SessionHeading } from '../../../components/Account/Session';
import Collapse from '../../../components/Collapse';
import AccountLayout from '../../../components/Layout/Account/AccountLayout';
import { TestTypeProgram } from '../../../constants';
import { CANCEL_APPOINTMENT } from '../../../graphql/mutations/Account/cancelAppointment';
import { RESEND_CONFIRMATION_EMAIL } from '../../../graphql/mutations/Account/resendConfirmationEmail';
import { INCOMING_APPOINTMENTS_SESSIONS } from '../../../graphql/queries/Account/appointmentsSessions';
import errorsHandler from '../../../helpers/errorsHandler';
import { useNotification, useQuery, useRouter } from '../../../hooks';
import { paths } from '../../../paths';

const IncomingAppointmentsSessions = () => {
  const { t } = useTranslation();
  const { lang, countryIso2 } = useRouter();
  const { newNotification } = useNotification();

  const filters = [
    {
      field: 'sessionStatus',
      operator: 'in',
      value: [
        'status_session_open_for_registration',
        'status_session_scheduled',
        'status_session_logistics_locked',
        'status_session_locked',
        'status_session_logistics_pending',
        'status_session_failed_orders',
        'status_session_pending_internal',
        'status_session_pending_external',
        'status_session_taken_into_account',
        'status_session_shipped',
        'status_session_delivered',
        'status_session_logistics_done',
        'status_session_preparing',
      ],
    },
    {
      field: 'status',
      operator: 'in',
      value: ['status_appointment_expected'],
    },
    {
      field: 'purchaseStatus',
      operator: 'in',
      value: ['status_purchase_billed', 'status_purchase_billed_without_invoice'],
    },
  ];

  const variablesQueryIncomingAppointmentsSessions = {
    filters,
    lang,
    country: countryIso2,
  };
  const {
    data: { appointmentsSessions: appointmentsSessionsList = [] },
    loading,
  } = useQuery(INCOMING_APPOINTMENTS_SESSIONS, {
    variables: variablesQueryIncomingAppointmentsSessions,
  });

  const [cancelAppointment] = useMutation(CANCEL_APPOINTMENT);
  const [resendConfirmationEmail] = useMutation(RESEND_CONFIRMATION_EMAIL);
  const appointmentsSessions = fromJS(appointmentsSessionsList);

  const onCancelAppointment = (sessionId, appointmentId) => {
    try {
      cancelAppointment({
        variables: { appointmentId },
        optimisticResponse: {
          __typename: 'Mutation',
          cancelAppointment: null,
        },
        update: (proxy) => {
          const { appointmentsSessions: incomingAppointmentSessions } = proxy.readQuery({
            query: INCOMING_APPOINTMENTS_SESSIONS,
            variables: variablesQueryIncomingAppointmentsSessions,
          });

          proxy.writeQuery({
            query: INCOMING_APPOINTMENTS_SESSIONS,
            variables: variablesQueryIncomingAppointmentsSessions,
            data: {
              appointmentsSessions: incomingAppointmentSessions.reduce((result, appointmentsSession) => {
                if (parseInt(appointmentsSession.openSession.id) !== parseInt(sessionId)) {
                  result.push(appointmentsSession);

                  return result;
                }

                const candidates = appointmentsSession.candidates.filter(
                  (candidate) => candidate.appointmentId !== appointmentId
                );

                if (candidates.length === 0) {
                  return result;
                }
                result.push({
                  candidates,
                  ...appointmentsSession,
                });

                return result;
              }, []),
            },
          });
        },
      }).then(() => {
        newNotification({
          content: t('account.sessions.candidate.message.appointment-canceled'),
          type: 'success',
        });
      });
    } catch (error) {
      errorsHandler({ error, newNotification, t });
    }
  };

  const onResendConfirmationEmail = (appointmentId) => {
    try {
      resendConfirmationEmail({
        variables: { appointmentId: appointmentId, locale: lang },
      }).then(() => {
        newNotification({
          content: t('account.sessions.candidate.message.resend-confirmation-email'),
          type: 'success',
        });
      });
    } catch (error) {
      errorsHandler({ error, newNotification, t });
    }
  };

  let content = <Loader />;

  if (!loading) {
    if (appointmentsSessions && appointmentsSessions.size > 0) {
      content = appointmentsSessions.map((appointmentsSession, key) => {
        const openSession = appointmentsSession.get('openSession');

        return (
          <Fragment key={key}>
            <AccountBlock center={true} className={'account-block--centered'}>
              <SessionHeading session={openSession} past={false} />
              <SessionDetail session={openSession} candidates={appointmentsSession.get('candidates')} />
              <Collapse
                title={t('account.incoming-open-sessions.registered-candidates')}
                content={appointmentsSession.get('candidates').map((candidate, key) => {
                  return (
                    <SessionCandidate
                      key={key}
                      candidate={candidate}
                      hasPhoto={
                        openSession.get('testType') &&
                        openSession.get('testType').get('program') === TestTypeProgram.PP &&
                        openSession.get('testType').get('isPhotoRequired')
                      }
                      past={false}
                      onCancelAppointment={onCancelAppointment}
                      onResendConfirmationEmail={onResendConfirmationEmail}
                      sessionId={openSession.get('id')}
                      sessionIsEproctored={!openSession.get('testingSite')}
                    />
                  );
                })}
              />
            </AccountBlock>
          </Fragment>
        );
      });
    } else {
      content = (
        <section className="account-block account-block--centered">
          <p className="account-block__empty-label">
            <Trans>account.incoming-open-sessions.no-tests</Trans>
          </p>
          <Button to={paths.SESSION_SEARCH} aria-label={t('account.incoming-open-sessions.book-test')}>
            <Trans>account.incoming-open-sessions.book-test</Trans>
          </Button>
        </section>
      );
    }
  }

  return <AccountLayout title={t('account.incoming-open-sessions.title')}>{content}</AccountLayout>;
};

export default IncomingAppointmentsSessions;
